import './index.scss'
import React, { useRef, useEffect, useState } from 'react';
import { Card, Form, Input, Button, Typography, Spin, message } from 'antd'
import logo from '@/assets/AspectLogo.png'
import { useDispatch } from 'react-redux'
import { fetchLogin, fetchUserInfo } from '@/store/modules/user'
import { useNavigate } from 'react-router-dom'
import LogUserActivity from '@/components/LogUserActivity';

const { Paragraph, Text } = Typography

// Configure global message positioning and behavior
message.config({
  top: '40%',         // Vertical center alignment
  duration: 2,
  maxCount: 3,        // Maximum number of messages displayed at once
  rtl: false,
})

const Login = () => {
  const inputRef = useRef(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [bidInfo, setBidInfo] = useState(null)
  const [loadingBid, setLoadingBid] = useState(true)
  const [loadingLogin, setLoadingLogin] = useState(false)  // Loading state for login action

  // Fetch current active/upcoming bid information on mount
  useEffect(() => {
    const fetchBid = async () => {
      try {
        const res = await fetch('https://dc28.job-bid.ca/server/api/current.php')
        const text = await res.text()
        let data = null
        try { data = JSON.parse(text) } 
        catch (err) { console.error('JSON Parse Error:', err); setBidInfo(null); return }
        if (data && data.success) setBidInfo(data.data)
        else setBidInfo(null)
      } catch (e) { console.error('Fetch Bid Error:', e); setBidInfo(null) }
      finally { setLoadingBid(false) }
    }
    fetchBid()
  }, [])

  // Calculate bid status and remaining days/countdown
  const getBidStatus = (bid) => {
    if (!bid) return { status: 'NONE' }
    const now = new Date()
    const start = new Date(bid.startDate.replace(' ', 'T'))
    const end = new Date(bid.endDate.replace(' ', 'T'))
    if (now < start) return { status: 'UPCOMING', days: Math.ceil((start - now) / (1000*60*60*24)) }
    if (now >= start && now <= end) return { status: 'OPEN', days: Math.ceil((end - now) / (1000*60*60*24)) }
    return { status: 'CLOSED' }
  }

  // Format date string for clean UI display
  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    return new Date(dateStr.replace(' ', 'T')).toLocaleDateString('en-CA', { year:'numeric', month:'short', day:'numeric' })
  }

  // Get corresponding label based on bid status
  const getStatusLabel = (status) => {
    switch(status) {
      case 'UPCOMING': return 'Days Until Start'
      case 'OPEN': return 'Days Left'
      default: return ''
    }
  }

  // Handle form submission and authentication
  const onFinish = async (values) => {
    try {
      setLoadingLogin(true)  // Start loading indicator
      await dispatch(fetchLogin(values))
      const user = await dispatch(fetchUserInfo())

      if (user && user.role) {
        message.success({ content: 'Login successful', duration: 2 })
        // if (user.role === 'Admin') navigate('/', { replace: true })
        if (user.role === 'Admin' || user.role === 'OM') {
          navigate('/', { replace: true })
        }
        else navigate('/Jobbid', { replace: true })
      } else {
        message.error({ content: 'User role not found', duration: 2 })
      }
    } catch (e) {
      console.error('Login Error:', e)
      
      // Extract specific error message whether it comes as a string or an object
      const errorMsg = typeof e === 'string' ? e : (e?.message || 'Invalid credentials or network error')

      message.error({ content: errorMsg, duration: 2 })
      setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select() }, 2000)
    } finally {
      setLoadingLogin(false)  // Stop loading indicator
    }
  }

  const bidStatus = getBidStatus(bidInfo)

  return (
    <div className="login">
      <Card className="login-container">

        <img className="login-logo" src={logo} alt="Logo" />
        <LogUserActivity category="client" />

        <Form onFinish={onFinish} validateTrigger="onBlur">

          {/* Employee ID Input */}
          <Form.Item
            name="employeeID"
            rules={[
              { required: true, message: 'Please enter your login ID' },
              { min: 5, message: 'Employee ID must be at least 5 characters' },
              { pattern: /^[a-zA-Z0-9]+$/, message: 'Letters and numbers only' }
            ]}
          >
            <Input ref={inputRef} size="large" placeholder="Employee ID" />
          </Form.Item>

          {/* Password Input */}
          <Form.Item
            name="code"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password size="large" placeholder="Password" />
          </Form.Item>

          {/* Login Submit Button */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loadingLogin} // Show loading state on button
              disabled={loadingLogin} // Disable during request to prevent double submission
            >
              {loadingLogin ? 'Logging in...' : 'Login'}
            </Button>
          </Form.Item>

          {/* BID Status Information Panel */}
          <div className="login-info">
            <Paragraph><Text strong>BID STATUS</Text></Paragraph>

            {loadingBid ? (
              <Spin tip="Loading bid..." fullscreen/>
            ) : !bidInfo ? (
              <Paragraph><Text type="secondary">No active bid</Text></Paragraph>
            ) : (
              <>
                <div className="bid-title">
                  {bidInfo.name || 'No Active Bid'}
                </div>
                <div className="bid-info-grid">

                  <div className="row">
                    <span className="label">Opens</span>
                    <span className="value">{formatDate(bidInfo.startDate)}</span>
                  </div>

                  <div className="row">
                    <span className="label">Ends</span>
                    <span className="value">{formatDate(bidInfo.endDate)}</span>
                  </div>

                  <div className="row">
                    <span className="label">Status</span>
                    <span className={`value status ${bidStatus.status.toLowerCase()}`}>
                      {bidStatus.status}
                    </span>
                  </div>

                  {/* Render countdown or remaining time row only when bid is not closed */}
                  {bidStatus.status !== 'CLOSED' && (
                    <div className="row">
                      <span className="label">
                        {bidStatus.status === 'UPCOMING' ? 'Countdown' : 'Remaining'}
                      </span>
                      <span className="value highlight">
                        {bidStatus.days} {getStatusLabel(bidStatus.status)}
                      </span>
                    </div>
                  )}

                </div>
              </>
            )}

            <Paragraph className="help-text">Need Help? Please see your Team Leader.</Paragraph>
          </div>

        </Form>
      </Card>
    </div>
  )
}

export default Login